import { CommonTokenStream, InputStream, Recognizer, Token } from 'antlr4';
import { ErrorListener } from 'antlr4/error/ErrorListener';
import { NQLLexer } from 'generated/nql/NQLLexer';
import { NQLParser } from 'generated/nql/NQLParser';

import { ParseQueryVisitor } from './parse-query-visitor';
import type { SearchCriterion, SearchQuery } from './search-query-model';
import {
  isCriterionComplete,
  isSearchCriterion,
  isSearchQuery,
  isSearchQueryComplete,
} from './search-query-model-utils';

class NqlSyntaxErrorListener implements ErrorListener {
  hasError = false;

  syntaxError(
    recognizer: Recognizer,
    offendingSymbol: Token,
    line: number,
    column: number,
    msg: string,
    e: unknown,
  ) {
    void recognizer;
    void offendingSymbol;
    void line;
    void column;
    void msg;
    void e;
    this.hasError = true;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  reportAmbiguity() {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  reportAttemptingFullContext() {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  reportContextSensitivity() {}
}

export function isValidNqlSyntax(nql?: string): boolean {
  if (!nql) {
    return true;
  }

  try {
    const inputStream = new InputStream(nql);
    const lexer = new NQLLexer(inputStream);
    const tokenStream = new CommonTokenStream(lexer);
    const parser = new NQLParser(tokenStream);
    const errorListener = new NqlSyntaxErrorListener();

    lexer.removeErrorListeners();
    lexer.addErrorListener(errorListener);
    parser.removeErrorListeners();
    parser.addErrorListener(errorListener);

    const tree = parser.parse();
    const parsedQuery = tree.accept(new ParseQueryVisitor()) as
      | SearchQuery
      | SearchCriterion
      | undefined;

    return !errorListener.hasError && isValidParsedQuery(parsedQuery);
  } catch {
    return false;
  }
}

function isValidParsedQuery(parsedQuery: SearchQuery | SearchCriterion | undefined): boolean {
  if (!parsedQuery) {
    return false;
  }

  if (isSearchQuery(parsedQuery)) {
    return isSearchQueryComplete(parsedQuery);
  }

  if (isSearchCriterion(parsedQuery)) {
    return isCriterionComplete(parsedQuery);
  }

  return false;
}
